'use client'
import React from 'react'
import Typography from 'src/components/design-system/Typography'
import { AuthorListItem } from 'src/components/authors/list/AuthorListItem'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import Button from 'src/components/design-system/Button'
import LINKS from 'src/_helpers/links'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { orderResourcesByUuid } from 'src/_helpers/orderResourcesByUuid'
const getAffiliations = (author) => {
  return orderResourcesByUuid(author?.affiliations, author?.affiliationOrder)
}
const getAddress = (author) => author?.address

export default function EditorialBoad({ block, locale, variant = 'journal', monograph = null }) {
  const translation = getPreferredTranslation(block, locale) || {}
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const isDomainJournal = variant === 'journal-domain'
  const title = translation?.title?.trim()
  const editorialGroups = (block.editorialGroups || [])
    .map((group) => ({
      ...group,
      editors: (group.editors || []).filter((editor) => editor?.data),
    }))
    .filter((group) => group.editors.length)
  const numberSlug = getPreferredTranslation(block.number, locale)?.slug || null
  const journalSlug = getPreferredTranslation(block.journal, locale)?.slug || null
  const monographSlug = getPreferredTranslation(monograph, locale)?.slug || null
  const routeMaps = variant === 'journal-domain' ? journalRoutes : null
  const linkEditorialBoard =
    variant === 'monograph'
      ? buildLocalizedPath(LINKS.MONOGRAPHS.EDITORIAL_BOARD, locale, { slug: monographSlug })
      : buildLocalizedPath(routeMaps?.ISSUES.EDITORIAL_BOARD || LINKS.ISSUES.EDITORIAL_BOARD, locale, {
          slug: journalSlug,
          issue: numberSlug,
        }) ||
        buildLocalizedPath(routeMaps?.JOURNALS.EDITORIAL_BOARD || LINKS.JOURNALS.EDITORIAL_BOARD, locale, {
          journal: journalSlug,
        })
  const buildColumnBorders = (index) => {
    const classes = []
    const tabletBorderClass = isDomainJournal ? 't:border-basic-gray8' : 't:border-dark-blue-dark-blue6'
    const desktopBorderClass = isDomainJournal ? 'd:border-basic-gray8' : 'd:border-dark-blue-dark-blue6'

    if (index % 2 === 1) {
      classes.push('t:border-l', tabletBorderClass, 't:pl-20')
    }

    if (index % 4 !== 0) {
      classes.push('d:border-l', desktopBorderClass, 'd:pl-20')
    }

    return classes.join(' ')
  }

  if (!editorialGroups.length) return null

  return (
    <section className={`my-64 d:mb-152 d:px-40 px-16 ${block.type.value}`} id={`${block.uuid}`}>
      {title && (
        <Typography
          variant="h2-caps-bold"
          color="text-basic-black"
          className="pb-32 t:pb-64 xl:max-w-[950px] max-w-665"
        >
          {title}
        </Typography>
      )}
      {editorialGroups.length > 0 ? (
        <div className="flex flex-col gap-y-48">
          {editorialGroups.slice(0, 2).map((section, key) => {
            const translationSection = getPreferredTranslation(section, locale) || {}
            const titleSection = translationSection?.title
            const editors = section.editors
            return (
              <div key={key}>
                {titleSection && (
                  <Typography variant="h3-caps-bold" color="text-dark-blue-dark-blue1" className="pb-40">
                    {titleSection}
                  </Typography>
                )}

                <div
                  className={`grid gap-24 border-y ${
                    isDomainJournal ? 'border-basic-gray8' : 'border-dark-blue-dark-blue6'
                  } t:grid-cols-2 d:grid-cols-4 py-24`}
                >
                  {editors.map((editor, i) => {
                    const columnBorders = buildColumnBorders(i)
                    const translationEditor = getPreferredTranslation(editor, locale) || {}
                    const position = translationEditor?.title
                    const dataEditor = editor.data
                    const orcid = dataEditor?.orcid
                    const fullname = dataEditor?.fullName
                    const affiliation = getAffiliations(dataEditor)
                    const address = getAddress(dataEditor)
                    const email = dataEditor?.email
                    const resolvedAffiliations = affiliation.length ? affiliation : []
                    const slug = dataEditor?.slug
                    const detailUrl =
                      buildLocalizedPath(routeMaps?.JOURNALS.EDITOR_DETAIL || LINKS.AUTHORS.DETAIL, locale, { slug }) ||
                      ' '
                    return (
                      <AuthorListItem
                        key={i}
                        name={fullname}
                        position={position}
                        orcid={orcid}
                        affiliations={resolvedAffiliations}
                        address={address}
                        email={email}
                        detailUrl={detailUrl}
                        locale={locale}
                        className={`pb-16 ${columnBorders}`}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      ) : null}
      {linkEditorialBoard && (journalSlug || monographSlug) && editorialGroups.length > 2 ? (
        <Button
          href={linkEditorialBoard}
          icon="right"
          size="l"
          className={`flex mx-auto relative w-fit mt-88 ${
            isDomainJournal ? '!border-basic-black !bg-transparent !text-basic-black hover:!bg-basic-gray8' : ''
          }`}
        >
          {translate('T_SEE_FULL_EDITORIAL_TEAM')}
        </Button>
      ) : null}
    </section>
  )
}
